import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { FileText, Plus, Edit, Trash2, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { notes } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import SectionHeader from '../UI/SectionHeader';
import { TextField, Autocomplete } from '@anyemp/ui-kit';
import SearchInput from '../UI/SearchInput';
import { noteTypes, getNoteTypeLabel, getNoteTypeColor } from '../../utils/noteTypeUtils';
import { useAuth } from '../../hooks/useAuth';
import ConfirmDialog from '../UI/ConfirmDialog';

const NoteForm = ({ formData, setFormData, onSubmit, onCancel, isNew }) => (
  <div className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Autocomplete
        fullWidth
        label="Note Type *"
        options={noteTypes}
        value={formData.note_type}
        onChange={(value) => setFormData(p => ({ ...p, note_type: value || '' }))}
        placeholder="Select note type"
      />
    </div>
    <div className="mt-3">
      <TextField
        label="Note"
        required
        multiline
        rows={4}
        fullWidth
        value={formData.note}
        onChange={(e) => setFormData(p => ({ ...p, note: e.target.value }))}
        placeholder="Enter your note..."
      />
    </div>
    <div className="flex gap-2 mt-3">
      <button
        type="button"
        onClick={onSubmit}
        className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {isNew ? 'Add Note' : 'Save'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary text-sm text-light-text-secondary dark:text-dark-text-secondary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
      >
        Cancel
      </button>
    </div>
  </div>
);

const NotesTab = forwardRef(({ applicationId, talentId, entityType: propEntityType, talentType, showAddButton = true }, ref) => {
  const { hasPermission } = useAuth();

  const entityType = propEntityType || (talentId ? 'Talent' : 'JobApplication');
  const entityId = talentId || applicationId;

  const notesResource = useMemo(() => {
    if (entityType === 'Talent' && talentType) return `${talentType}_notes`;
    return 'job_application';
  }, [entityType, talentType]);

  const canCreate = useMemo(() => hasPermission('talent-service', notesResource, 'create'), [hasPermission, notesResource]);
  const canUpdate = useMemo(() => hasPermission('talent-service', notesResource, 'update'), [hasPermission, notesResource]);
  const canDelete = useMemo(() => hasPermission('talent-service', notesResource, 'delete'), [hasPermission, notesResource]);

  // null = closed | { id: 'new' } = add form at top | { id: X, ... } = edit inline
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ note_type: '', note: '' });
  const [notesList, setNotesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // note to delete
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [overflowingNotes, setOverflowingNotes] = useState(new Set());
  const noteTextRefs = useRef({});

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ note_type: '', search: '' });

  const handleCreate = () => {
    setEditingNote({ id: 'new' });
    setFormData({ note_type: '', note: '' });
  };

  useImperativeHandle(ref, () => ({ openForm: handleCreate }));

  useEffect(() => { fetchNotes(); }, [entityId, entityType, page, filters.note_type, filters.search]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...(filters.note_type && { note_type: filters.note_type }),
        ...(filters.search && { search: filters.search })
      };
      const response = await notes.getNotesByEntity(entityType, entityId, params);
      if (response.success) {
        setNotesList(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      } else {
        setError('Failed to fetch notes');
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Error loading notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.note_type || !formData.note?.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingNote?.id === 'new') {
        await notes.createNote({
          entityType,
          entityId: parseInt(entityId),
          note_type: formData.note_type,
          note: formData.note
        });
      } else {
        await notes.updateNote(editingNote.id, {
          note_type: formData.note_type,
          note: formData.note
        });
      }
      setEditingNote(null);
      setFormData({ note_type: '', note: '' });
      fetchNotes();
    } catch (err) {
      console.error('Error saving note:', err);
      alert('Error saving note');
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({ note_type: note.note_type, note: note.note });
  };

  const handleDelete = (note) => {
    setDeleteConfirm(note);
  };

  const confirmDelete = async () => {
    try {
      await notes.deleteNote(deleteConfirm.id);
      fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Error deleting note');
    }
  };

  const handleCancel = () => {
    setEditingNote(null);
    setFormData({ note_type: '', note: '' });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const toggleExpand = (id) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  useLayoutEffect(() => {
    const overflowing = new Set();
    notesList.forEach(({ id }) => {
      const el = noteTextRefs.current[id];
      if (el && el.scrollHeight > el.clientHeight) overflowing.add(id);
    });
    setOverflowingNotes(overflowing);
  }, [notesList]);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const filterNoteTypes = [{ value: '', label: 'All Types' }, ...noteTypes];

  return (
    <>
    <div className={showAddButton ? 'bg-light-bg-primary p-4 dark:bg-dark-bg-primary sm:p-6' : ''}>
      {showAddButton && (
        <SectionHeader
          icon={FileText}
          title={total > 0 ? `Notes (${total})` : 'Notes'}
          action={canCreate && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Note
            </button>
          )}
        />
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search notes..."
            value={filters.search}
            onChange={(v) => handleFilterChange('search', v)}
          />
        </div>
        <div className="sm:w-48">
          <Autocomplete
            clearable
            fullWidth
            options={filterNoteTypes}
            value={filters.note_type}
            onChange={(value) => handleFilterChange('note_type', value || '')}
            placeholder="All Types"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add form — appears at top */}
      {editingNote?.id === 'new' && (
        <div className="mb-3">
          <NoteForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isNew
          />
        </div>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader variant="component" size="md" fullHeight={false} />
        </div>
      ) : notesList.length === 0 ? (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-4">
          {filters.search || filters.note_type ? 'No notes match your filters' : 'No notes yet'}
        </p>
      ) : (
        <>
          <div className="space-y-3">
            {notesList.map((note) => {
              if (editingNote?.id === note.id) {
                return (
                  <NoteForm
                    key={note.id}
                    formData={formData}
                    setFormData={setFormData}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    isNew={false}
                  />
                );
              }

              const isExpanded = expandedNotes.has(note.id);
              const showToggle = overflowingNotes.has(note.id) || isExpanded;

              return (
                <div key={note.id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-md text-white mb-1"
                        style={{ backgroundColor: getNoteTypeColor(note.note_type) }}
                      >
                        {getNoteTypeLabel(note.note_type)}
                      </span>
                      <div
                        ref={el => { noteTextRefs.current[note.id] = el; }}
                        className={`text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap break-words${isExpanded ? '' : ' line-clamp-[10]'}`}
                      >
                        {note.note}
                      </div>
                      {showToggle && (
                        <button onClick={() => toggleExpand(note.id)} className="text-primary-600 dark:text-primary-400 hover:underline text-xs mt-1">
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        {note.created_at && (
                          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex items-center gap-1">
                            <Clock className="w-3 h-3" />{formatDate(note.created_at)}
                          </span>
                        )}
                        {note.updated_at && note.updated_at !== note.created_at && (
                          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex items-center gap-1">
                            <Clock className="w-3 h-3" />Updated: {formatDate(note.updated_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {(canUpdate || canDelete) && (
                      <div className="flex items-center gap-1 shrink-0">
                        {canUpdate && (
                          <button onClick={() => handleEdit(note)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Edit">
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDelete(note)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-light-border-primary dark:border-dark-border-primary pt-4">
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} notes
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Note"
      message="Are you sure you want to delete this note? This action cannot be undone."
      confirmText="Delete"
    />
    </>
  );
});

export default NotesTab;
